import { AnyAction, combineReducers } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { accessControlReducer } from './accessControlReducer';
import { accountingFieldsReducer } from './accountingFields';
import aggregationCounts from './aggregationCountsReducer';
import { alternateEmails } from './alternate_emails';
import { bulkUploadReducer } from './bulk_upload';
import { cardPageReducer } from './cardPageReducer';
import { cardRequestsReducer } from './cardRequestsReducer';
import { cardStatementUploadReducer as cardStatementUpload } from './card_statement_upload';
import { cardsGatewayIssuingModalReducer } from './cardsGatewayIssuingModalReducer';
import { cardsReducer } from './cardsReducer';
import { categoriesReducer } from './categoriesReducer';
import { companyReducer } from './company';
import { connectionsReducer } from './connectionsReducer';
import { currenciesReducer } from './currenciesReducer';
import { currentUserReducer } from './currentUserReducer';
import { expenseBucketsReducer } from './expenseBucketsReducer';
import { expenseTagBucketsReducer } from './expenseBucketsWithTagsReducer';
import { expenseListReducer } from './expenseListReducer';
import managersReducer from './managersReducer';
import { modalReducer } from './modalReducer';
import { oauthTokensPageReducer } from './oauthTokensPage';
import { peopleReducer } from './peopleReducer';
import { receiptsReducer } from './receiptsReducer';
import { reimbursableExpensesReducer } from './reimbursableExpensesReducer';
import searchReducer from './search';
import { sidebarCountersReducer } from './sidebarCountersReducer';
import { sidebarReducer } from './sidebarReducer';
import { suggestionsReducer } from './suggestionsReducer';
import { transactionsReducer } from './transactionsReducer';
import { uiPreferencesReducer } from './uiPreferencesReducer';
import { userProfilesReducer } from './userProfilesReducer';
import { usersReducer } from './usersReducer';

export type AppThunkDispatch = ThunkDispatch<IAppStateContainer, {}, AnyAction>;
export type AppThunkGetState = () => IAppStateContainer;

export interface IAppStateContainer {
  devTools: boolean;
  cards: ReturnType<typeof cardsReducer>;
  cardsGatewayIssuingModal: ReturnType<typeof cardsGatewayIssuingModalReducer>;
  categories: ReturnType<typeof categoriesReducer>;
  sidebar?: ReturnType<typeof sidebarReducer>;
  sidebarCounters: ReturnType<typeof sidebarCountersReducer>;
  currentUser: ReturnType<typeof currentUserReducer>;
  expenseBucketsWithTags: ReturnType<typeof expenseTagBucketsReducer>;
  expenseBuckets: ReturnType<typeof expenseBucketsReducer>;
  expenseList: ReturnType<typeof expenseListReducer>;
  modal: ReturnType<typeof modalReducer>;
  receipts: ReturnType<typeof receiptsReducer>;
  users: ReturnType<typeof usersReducer>;
  userProfiles: ReturnType<typeof userProfilesReducer>;
  company: ReturnType<typeof companyReducer>;
  bulkUpload: ReturnType<typeof bulkUploadReducer>;
  managers: ReturnType<typeof managersReducer>;
  cardRequests: ReturnType<typeof cardRequestsReducer>;
  cardPage: ReturnType<typeof cardPageReducer>;
  accessControl: ReturnType<typeof accessControlReducer>;
  transactions: ReturnType<typeof transactionsReducer>;
  aggregationCounts: ReturnType<typeof aggregationCounts>;
  currencies: ReturnType<typeof currenciesReducer>;
  oauthTokensPage: ReturnType<typeof oauthTokensPageReducer>;
  reimbursableExpenses: ReturnType<typeof reimbursableExpensesReducer>;
  connections: ReturnType<typeof connectionsReducer>;
  accountingFields: ReturnType<typeof accountingFieldsReducer>;
  search: ReturnType<typeof searchReducer>;
  suggestions: ReturnType<typeof suggestionsReducer>;
  alternateEmails: ReturnType<typeof alternateEmails>;
  cardStatementUpload: ReturnType<typeof cardStatementUpload>;
  uiPreferences: ReturnType<typeof uiPreferencesReducer>;
  people: ReturnType<typeof peopleReducer>;
}

const reducers = {
  devTools: (state = Boolean(window.FEATURES?.use_redux_devtools)) => state,
  sidebar: sidebarReducer,
  sidebarCounters: sidebarCountersReducer,
  accessControl: accessControlReducer,
  accountingFields: accountingFieldsReducer,
  aggregationCounts,
  bulkUpload: bulkUploadReducer,
  cardPage: cardPageReducer,
  cardRequests: cardRequestsReducer,
  cards: cardsReducer,
  cardsGatewayIssuingModal: cardsGatewayIssuingModalReducer,
  categories: categoriesReducer,
  company: companyReducer,
  connections: connectionsReducer,
  currencies: currenciesReducer,
  currentUser: currentUserReducer,
  expenseBuckets: expenseBucketsReducer,
  expenseBucketsWithTags: expenseTagBucketsReducer,
  expenseList: expenseListReducer,
  managers: managersReducer,
  modal: modalReducer,
  oauthTokensPage: oauthTokensPageReducer,
  receipts: receiptsReducer,
  reimbursableExpenses: reimbursableExpensesReducer,
  search: searchReducer,
  suggestions: suggestionsReducer,
  userProfiles: userProfilesReducer,
  users: usersReducer,
  transactions: transactionsReducer,
  alternateEmails: alternateEmails,
  cardStatementUpload,
  uiPreferences: uiPreferencesReducer,
  people: peopleReducer
};

export const rootReducer = combineReducers<IAppStateContainer>(reducers);
